// Power Calculations Module - Pure physics-based calculations
// No caching, no database queries - just calculations

import { ParameterInfo } from './telemetryService';
import * as strokeAnalysis from './strokeAnalysis';
import { DEBUG } from '../../config/debug';
import { getLeverMode } from '../../config/experiments';

export interface PowerCalculationResult {
  timeSeries: Array<{ time: number; [rower: string]: number }>;
  strokeCount: number;
  strokeBoundaries: Array<{ start: number; end: number | null }>;
  workBreakdown: {
    [rower: string]: { drivePhaseWork: number; recoveryPhaseWork: number; totalWork: number };
  };
  strokeAverages: {
    [rower: string]: Array<{ duration: number; totalWork: number; averagePower: number }>;
  };
}

export interface FullSessionPowerResult {
  averagePowerByRower: { [rower: number]: number };
  overallAverage: number;
  enhancedRowerData: Array<{
    id: number;
    power: number;
    min: number;
    max: number;
    p25: number;
    p75: number;
    peak: number;
    consistency: number;
    powerDistribution: Array<{
      start: number;
      end: number;
      count: number;
      frequency: number;
    }>;
  }>;
  powerRange: { min: number; max: number };
  peakPower: number;
  consistency: number;
}

// Pure power calculation functions extracted from telemetryService
export function calculateStrokeIntegratedPower(
  telemetryData: any[],
  startTime: number,
  parameters: ParameterInfo
): PowerCalculationResult {
  DEBUG.log('POWER', '🔍 GLYYD_POWER_CALC_001: calculateStrokeIntegratedPower called');
  DEBUG.log('POWER', '🔍 GLYYD_POWER_CALC_002: Input data stats:', {
    telemetryDataLength: telemetryData.length,
    startTime: startTime,
    sampleDataPoint: telemetryData[0] || null,
    parameters: parameters,
  });

  // Initialize result structure
  const result = {
    timeSeries: [] as Array<{ time: number; [rower: string]: number }>,
    strokeCount: 0,
    strokeBoundaries: [] as Array<{ start: number; end: number | null }>,
    workBreakdown: {} as {
      [rower: string]: { drivePhaseWork: number; recoveryPhaseWork: number; totalWork: number };
    },
    strokeAverages: {} as {
      [rower: string]: Array<{ duration: number; totalWork: number; averagePower: number }>;
    },
  };

  if (!parameters || telemetryData.length === 0) {
    DEBUG.log(
      'POWER',
      '🔍 GLYYD_POWER_CALC_003: No parameters or telemetry data - returning empty result'
    );
    return result;
  }

  DEBUG.log('POWER', '🔍 GLYYD_POWER_CALC_004: Processing with parameters:', {
    isScull: parameters.scull_oar_length !== null,
    oarLength:
      (parameters.scull_oar_length !== null
        ? parameters.scull_oar_length
        : parameters.sweep_oar_length) || 376,
    inboard:
      (parameters.scull_oar_length !== null
        ? parameters.scull_oar_inboard
        : parameters.sweep_oar_inboard) || 115,
    driveThreshold:
      (parameters.scull_oar_length !== null
        ? parameters.scull_drive_start
        : parameters.sweep_drive_start) || 30,
  });

  // Determine boat type and get session-specific parameters
  const isScull = parameters.scull_oar_length !== null;
  const oarLength = (isScull ? parameters.scull_oar_length : parameters.sweep_oar_length) || 376;
  const inboard = (isScull ? parameters.scull_oar_inboard : parameters.sweep_oar_inboard) || 115;
  const driveThreshold =
    (isScull ? parameters.scull_drive_start : parameters.sweep_drive_start) || 30;

  // Convert to SI units
  const oarLengthM = oarLength / 100; // cm to meters
  const inboardM = inboard / 100; // cm to meters
  const outboardM = oarLengthM - inboardM;

  // Physics constants
  const TIME_STEP = 0.02; // 50Hz sampling
  const KGF_TO_N = 9.80665; // kilogram-force to Newtons conversion

  // Group data by rower
  const rowerData: { [rower: string]: any[] } = {};
  telemetryData.forEach((point: any) => {
    const rowerKey = `Rower ${point.rower_position}`;
    if (!rowerData[rowerKey]) {
      rowerData[rowerKey] = [];
      result.workBreakdown[rowerKey] = { drivePhaseWork: 0, recoveryPhaseWork: 0, totalWork: 0 };
      result.strokeAverages[rowerKey] = [];
    }
    rowerData[rowerKey].push(point);
  });

  let maxStrokes = 0;

  // Process each rower's data
  const leverMode = getLeverMode();
  Object.entries(rowerData).forEach(([rowerKey, points]) => {
    if (points.length === 0) return;

    DEBUG.log(
      'POWER',
      `🔍 GLYYD_POWER_CALC_005: Processing ${rowerKey} with ${points.length} data points`
    );

    // Sort by timestamp
    points.sort((a, b) => a.timestamp - b.timestamp);

    let inDrivePhase = false;
    let currentStrokeStart: number | null = null;
    let currentStrokeWork = 0;
    let strokeCount = 0;
    let sampleCalculationLogged = false;

    points.forEach((point: any, index: number) => {
      const relativeTime = (point.timestamp - startTime) / 1000; // Convert to seconds

      // Get sensor readings
      const gateForceKgf = Math.abs(point.gate_force_x || 0);
      const angularVelDeg = point.gate_angle_velocity || 0;
      const oarAngleDeg = point.gate_angle || 0;

      // UNIT CONVERSIONS TO SI (International System of Units)
      const forceNewtons = gateForceKgf * KGF_TO_N; // kgf → N (Newtons)
      const angularVelRad = angularVelDeg * (Math.PI / 180); // deg/s → rad/s
      const angleRad = oarAngleDeg * (Math.PI / 180); // deg → rad

      // 1. FORCE TRANSFORMATION (lever-mode aware)
      // If leverMode === 'gate', convert gate force to handle force via (outboard / inboard).
      // If leverMode === 'handle', treat GateForceX as handle-equivalent and do not scale.
      const forceMultiplier = leverMode === 'gate' ? outboardM / inboardM : 1;
      const handleForce = forceNewtons * forceMultiplier;

      // 2. VELOCITY TRANSFORMATION WITH ANGLE CORRECTION
      // Full physics: v = r × ω × cos(θ)
      // Where θ is oar angle relative to boat centerline
      const handleSpeed = inboardM * angularVelRad * Math.cos(angleRad);

      // 3. INSTANTANEOUS POWER WITH DRIVE PHASE FILTERING
      // Only positive handle speed indicates drive phase (power application)
      const instantPower = handleSpeed > 0 ? Math.abs(handleForce * handleSpeed) : 0;

      // Log first few calculations for debugging
      if (!sampleCalculationLogged && index < 3 && instantPower > 0) {
        DEBUG.log('POWER', `🔍 GLYYD_POWER_CALC_006: Sample calculation for ${rowerKey}:`, {
          timestamp: point.timestamp,
          relativeTime: relativeTime,
          gateForceKgf: gateForceKgf,
          angularVelDeg: angularVelDeg,
          oarAngleDeg: oarAngleDeg,
          forceNewtons: forceNewtons,
          handleForce: handleForce,
          handleSpeed: handleSpeed,
          instantPower: instantPower,
          oarLengthM: oarLengthM,
          outboardM: outboardM,
          inboardM: inboardM,
        });
        sampleCalculationLogged = true;
      }

      // Stroke boundary detection
      if (!inDrivePhase && gateForceKgf >= driveThreshold) {
        // Start of new drive phase
        if (currentStrokeStart !== null && strokeCount > 0) {
          // Complete previous stroke
          const strokeDuration = (point.timestamp - currentStrokeStart) / 1000;
          const averagePower = strokeDuration > 0 ? currentStrokeWork / strokeDuration : 0;

          result.strokeAverages[rowerKey].push({
            duration: strokeDuration,
            totalWork: currentStrokeWork,
            averagePower: averagePower,
          });

          // Add stroke boundary
          if (result.strokeBoundaries.length === strokeCount - 1) {
            result.strokeBoundaries.push({ start: currentStrokeStart, end: point.timestamp });
          }
        }

        inDrivePhase = true;
        currentStrokeStart = point.timestamp;
        currentStrokeWork = 0;
        strokeCount++;
      } else if (inDrivePhase && gateForceKgf < driveThreshold * 0.5) {
        // End of drive phase detected
        // Using 50% of start threshold (e.g., 15 kgf for default 30 kgf start)
        // This hysteresis prevents false transitions during force fluctuations
        inDrivePhase = false;
      }

      // Accumulate work during drive phase only
      if (inDrivePhase && instantPower > 0) {
        const work = instantPower * TIME_STEP; // Work = Power × Time
        currentStrokeWork += work;
        result.workBreakdown[rowerKey].drivePhaseWork += work;
        result.workBreakdown[rowerKey].totalWork += work;
      }

      // Add to time series
      const existingPoint = result.timeSeries.find((p) => Math.abs(p.time - relativeTime) < 0.01);
      if (existingPoint) {
        existingPoint[rowerKey] = instantPower;
      } else {
        const newPoint: { time: number; [key: string]: number } = { time: relativeTime };
        newPoint[rowerKey] = instantPower;
        result.timeSeries.push(newPoint);
      }
    });

    maxStrokes = Math.max(maxStrokes, strokeCount);
  });

  // Handle final stroke (no end detected)
  if (result.strokeBoundaries.length < maxStrokes) {
    const lastStart =
      result.strokeBoundaries[result.strokeBoundaries.length - 1]?.start || startTime;
    result.strokeBoundaries.push({ start: lastStart, end: null });
  }

  // Sort time series by time
  result.timeSeries.sort((a, b) => a.time - b.time);
  result.strokeCount = maxStrokes;

  return result;
}

export function calculateFullSessionAveragePower(
  telemetryData: any[],
  parameters: ParameterInfo | null
): FullSessionPowerResult {
  if (!telemetryData || telemetryData.length === 0) {
    return {
      averagePowerByRower: {},
      overallAverage: 0,
      enhancedRowerData: [] as Array<{
        id: number;
        power: number;
        min: number;
        max: number;
        p25: number;
        p75: number;
        peak: number;
        consistency: number;
        powerDistribution: Array<{
          start: number;
          end: number;
          count: number;
          frequency: number;
        }>;
      }>,
      powerRange: { min: 0, max: 0 },
      peakPower: 0,
      consistency: 0,
    };
  }

  // Use session-specific parameters or fallback to standard sweep oar dimensions
  const oarLength = parameters?.sweep_oar_length || 376.0; // cm - total oar length
  const inboard = parameters?.sweep_oar_inboard || 115.0; // cm - handle to oarlock
  const outboard = oarLength - inboard; // cm - oarlock to blade tip

  // Get standardized drive threshold from parameters (consistent with strokeAnalysis)
  const driveThreshold = parameters?.sweep_drive_start || parameters?.scull_drive_start || 30;

  // Convert to SI units for physics calculations (CRITICAL for accuracy)
  const inboardM = inboard / 100; // cm to meters
  const outboardM = outboard / 100; // cm to meters

  // Physics constants (NEVER change these without understanding implications)
  const KGF_TO_N = 9.80665; // Standard gravity: kilogram-force to Newtons conversion

  // Use strokeAnalysis to detect proper stroke boundaries for consistent phase detection
  const strokeBoundaries = strokeAnalysis.detectStrokeBoundaries(telemetryData, driveThreshold);

  // Calculate power for each data point and group by rower, filtering by drive phase
  const rowerPowers: { [rower: number]: number[] } = {};

  const leverMode = getLeverMode();
  telemetryData.forEach((point: any) => {
    if (!rowerPowers[point.rower_position]) {
      rowerPowers[point.rower_position] = [];
    }

    // Get raw sensor readings (units: kgf, degrees/second)
    const gateForceKgf = Math.abs(point.gate_force_x || 0);
    const angularVelDeg = Math.abs(point.gate_angle_velocity || 0);

    // UNIT CONVERSIONS (CRITICAL - wrong units = wrong physics)
    const forceNewtons = gateForceKgf * KGF_TO_N; // kgf → N
    const angularVelRad = angularVelDeg * (Math.PI / 180); // deg/s → rad/s

    // ROWING PHYSICS CALCULATIONS:

    // 1. FORCE TRANSFORMATION (lever-mode aware)
    const forceMultiplier = leverMode === 'gate' ? outboardM / inboardM : 1;
    const handleForce = forceNewtons * forceMultiplier;

    // 2. VELOCITY TRANSFORMATION: Angular velocity to linear handle speed
    // Physics: v = ωr (linear velocity = angular velocity × radius)
    // Simplified: ignores oar angle for average power calculation
    const handleSpeed = inboardM * angularVelRad;

    // 3. POWER CALCULATION: Fundamental physics P = F × v
    // Result in Watts (Joules/second) - the rate of energy transfer
    const power = handleForce * handleSpeed;

    rowerPowers[point.rower_position].push(power);
  });

  // Calculate averages
  const averagePowerByRower: { [rower: number]: number } = {};
  let totalPowerSum = 0;
  let rowerCount = 0;

  Object.entries(rowerPowers).forEach(([rower, powers]) => {
    // REVERT: Use original average calculation (don't filter for averages)
    const avg = powers.reduce((a, b) => a + b, 0) / powers.length;
    averagePowerByRower[Number(rower)] = Math.round(avg);

    totalPowerSum += avg;
    rowerCount++;
  });

  const overallAverage = rowerCount > 0 ? Math.round(totalPowerSum / rowerCount) : 0;

  // Calculate enhanced power statistics

  // 1. Power range from rower averages
  const rowerAverages = Object.values(averagePowerByRower);
  const powerRange =
    rowerAverages.length > 0
      ? { min: Math.min(...rowerAverages), max: Math.max(...rowerAverages) }
      : { min: 0, max: 0 };

  // 2. Peak power across all data points - CHANGED TO TOTAL CREW PEAK
  let peakPower = 0;

  // Find the maximum total crew power at any single moment
  // We need to find the time point where the sum of all rowers' power was highest
  const allTimePoints = new Set<number>();
  Object.values(rowerPowers).forEach((powers, _rowerIndex) => {
    powers.forEach((_, powerIndex) => {
      allTimePoints.add(powerIndex);
    });
  });

  // For each time point, sum all rowers' power and find the maximum
  Array.from(allTimePoints).forEach((timeIndex) => {
    let totalPowerAtThisTime = 0;
    Object.values(rowerPowers).forEach((powers) => {
      if (powers[timeIndex] !== undefined) {
        totalPowerAtThisTime += powers[timeIndex];
      }
    });
    if (totalPowerAtThisTime > peakPower) {
      peakPower = totalPowerAtThisTime;
    }
  });

  // 3. Consistency percentage (average of per-rower coefficients of variation)
  let consistency = 0;
  const individualRowerConsistencies: { [rower: string]: number } = {}; // Store individual consistencies
  if (Object.keys(rowerPowers).length > 0) {
    const rowerConsistencies: number[] = [];

    Object.entries(rowerPowers).forEach(([rowerPosition, powers]) => {
      if (powers.length > 1) {
        // Use strokeAnalysis-detected boundaries to filter for drive phase only
        // This ensures consistency calculation only uses power during actual drive phases
        const rowerTelemetryData = telemetryData.filter(
          (point) => point.rower_position === Number(rowerPosition)
        );

        const drivePhasePowers: number[] = [];

        // For each detected stroke boundary, collect power values during drive phase
        strokeBoundaries.forEach((boundary) => {
          const strokeData = rowerTelemetryData.filter((point) => {
            const timestamp = point.timestamp;
            return (
              timestamp >= boundary.start && (boundary.end === null || timestamp <= boundary.end)
            );
          });

          // Filter for drive phase within this stroke (force >= driveThreshold)
          const drivePhaseData = strokeData.filter((point) => {
            const gateForceKgf = Math.abs(point.gate_force_x || 0);
            return gateForceKgf >= driveThreshold;
          });

          // Calculate power for drive phase data points
          drivePhaseData.forEach((point) => {
            const gateForceKgf = Math.abs(point.gate_force_x || 0);
            const angularVelDeg = Math.abs(point.gate_angle_velocity || 0);

            const forceNewtons = gateForceKgf * KGF_TO_N;
            const angularVelRad = angularVelDeg * (Math.PI / 180);
            const handleForce = forceNewtons * (outboardM / inboardM);
            const handleSpeed = inboardM * angularVelRad;
            const power = handleForce * handleSpeed;

            if (power > 0) {
              drivePhasePowers.push(power);
            }
          });
        });

        if (drivePhasePowers.length > 1) {
          const mean = drivePhasePowers.reduce((sum, p) => sum + p, 0) / drivePhasePowers.length;
          const variance =
            drivePhasePowers.reduce((sum, p) => sum + Math.pow(p - mean, 2), 0) /
            drivePhasePowers.length;
          const stdDev = Math.sqrt(variance);

          // Coefficient of variation (lower is more consistent)
          const coefficientOfVariation = mean > 0 ? stdDev / mean : 1;

          // Convert to consistency percentage (higher is more consistent)
          const rowerConsistency = Math.max(0, Math.min(100, (1 - coefficientOfVariation) * 100));

          rowerConsistencies.push(rowerConsistency);
          // Store individual consistency for use in enhancedRowerData
          individualRowerConsistencies[rowerPosition] = rowerConsistency;
        } else {
          individualRowerConsistencies[rowerPosition] = 0;
        }
      } else {
      }
    });

    consistency =
      rowerConsistencies.length > 0
        ? Math.round(rowerConsistencies.reduce((sum, c) => sum + c, 0) / rowerConsistencies.length)
        : 0;
  } else {
  }

  // 4. Individual enhanced rower statistics
  const enhancedRowerData = Object.entries(rowerPowers)
    .map(([rowerPosition, powers]) => {
      const rowerPosNum = parseInt(rowerPosition);

      // Sort powers for percentile calculations
      const sortedPowers = [...powers].sort((a, b) => a - b);
      const p25Index = Math.floor(sortedPowers.length * 0.25);
      const p75Index = Math.floor(sortedPowers.length * 0.75);

      // Use the strokeAnalysis-based consistency calculation instead of old method
      const rowerConsistency = individualRowerConsistencies[rowerPosition.toString()] || 0;

      // CREATE POWER DISTRIBUTION HISTOGRAM for proper density visualization
      const createPowerHistogram = (powerValues: number[]) => {
        if (powerValues.length === 0) return [];

        // SEPARATE: Drive phase power for density vs full cycle for average
        const drivePhasePowers = powerValues.filter((p) => p > 50);

        if (drivePhasePowers.length === 0) return [];

        // Focus density around SESSION average (matches black line)
        const sessionAverage = averagePowerByRower[parseInt(rowerPosition)] || 0;

        // Use FIXED window around SESSION average (aligns with black line)
        const windowSize = 200; // ±200W window around SESSION average
        const min = Math.max(0, sessionAverage - windowSize); // Don't go negative
        const max = sessionAverage + windowSize;
        const range = max - min; // 400W total range (±200W)
        const binCount = 12; // 400W ÷ 12 = 33W per bin
        const binWidth = range / binCount;

        const bins = Array(binCount)
          .fill(0)
          .map((_, i) => ({
            start: min + i * binWidth,
            end: min + (i + 1) * binWidth,
            count: 0,
            frequency: 0,
          }));

        // Count ALL powers within session-aligned range (matches session average calculation)
        powerValues.forEach((power) => {
          if (power >= min && power <= max) {
            const binIndex = Math.min(binCount - 1, Math.floor((power - min) / binWidth));
            bins[binIndex].count++;
          }
        });

        // Calculate frequencies (0-1)
        const maxCount = Math.max(...bins.map((b) => b.count));
        bins.forEach((bin) => {
          bin.frequency = maxCount > 0 ? bin.count / maxCount : 0;
        });

        return bins;
      };

      const powerDistribution = createPowerHistogram(powers);

      return {
        id: rowerPosNum,
        power: averagePowerByRower[rowerPosNum] || 0,
        min: Math.round(Math.min(...powers)),
        max: Math.round(Math.max(...powers)),
        p25: Math.round(sortedPowers[p25Index] || 0),
        p75: Math.round(sortedPowers[p75Index] || 0),
        peak: Math.round(Math.max(...powers)), // Peak should be maximum actual power
        consistency: Math.round(rowerConsistency),
        powerDistribution: powerDistribution, // NEW: Real power distribution for density visualization
      };
    })
    .sort((a, b) => a.id - b.id); // Sort by rower position

  return {
    averagePowerByRower,
    overallAverage,
    enhancedRowerData,
    powerRange,
    peakPower: Math.round(peakPower),
    consistency,
  };
}
